import React, {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useId,
  useState,
} from 'react';
import clsx from 'clsx';
import { MediumHeader } from '../../ui/Header/Header';
import Button from '../../ui/Button/BlockButton';
import TextArea from '../../ui/TextArea/TextArea';
import TextInput, { SupportedInputType } from '../../ui/TextInput/TextInput';
import TextLink, { TextLinkTargetType } from '../../ui/TextLink/TextLink';
import PanelContentHeader from '../../ui/PanelContentHeader/PanelContentHeader';
import StarRating from './StarRating/StarRating';
import { isSubmitDisabled } from './lib/submitGate';
import { useTheme } from '../../../hooks/useTheme';
import { useSettings } from '../../../hooks/useSettings';
import { useTranslations } from '../../../hooks/useTranslations';
import styles from './Landscape.module.css';

export interface LandscapeProps {
  featureName?: string;
  selectedRating: number;
  onRatingSelect: (index: number) => void;
  onClickSupportLink?: () => void;
  submitFeedback?: (feedbackComment: string) => void;
  commentOptional?: boolean;
  enablePanelHeader: boolean;
  collapsible?: boolean;
}

const LEFT = 'left';

const Landscape = ({
  featureName,
  selectedRating,
  onRatingSelect,
  onClickSupportLink,
  submitFeedback,
  commentOptional,
  enablePanelHeader,
  collapsible = false,
}: LandscapeProps) => {
  const {
    feedbackButtonText = '',
    feedbackButtonTextShort = '',
    feedbackChangeStarRating = '',
    feedbackCommentLabel = '',
    feedbackCustomerSupportLink = '',
    feedbackExperienceHeader = '',
    feedbackExperienceBodyText = '',
  } = useTranslations();

  const {
    linksNoneDefault: textLinkStyle,
    linksNoneHover: textLinkHoverStyle,
  } = useTheme();

  const { feedback } = useSettings();

  const { customerSupportLink } = feedback ?? {};

  const [feedbackComment, setFeedbackComment] = useState('');
  const [collapsed, setCollapsed] = useState(collapsible);
  const [interactiveExperienceHeader, setInteractiveExperienceHeader] =
    useState(feedbackExperienceHeader);
  const commentId = useId();

  const handleCommentChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setFeedbackComment(event.target.value);

  const handleSubmit = () => {
    if (submitFeedback) submitFeedback(feedbackComment);
  };

  const handleRatingSelect = (index: number, selectedText?: string) => {
    onRatingSelect(index);
    setInteractiveExperienceHeader(selectedText ?? feedbackExperienceHeader);
  };

  const handleStarKeyDown = (
    event: KeyboardEvent,
    index: number,
    selectedText?: string,
  ) => {
    const keyCode = event.nativeEvent.code;
    if (keyCode === 'Enter' || keyCode === 'Space' || keyCode === 'Tab') {
      setCollapsed(false);
      handleRatingSelect(index, selectedText);
    }
  };

  const handleStarClick = (
    event: MouseEvent<HTMLInputElement>,
    index: number,
    selectedText?: string,
  ) => {
    // In the collapsible layout, keyboard selection fires a synthetic click
    // (detail === 0); ignore it so arrow-key navigation does not expand the
    // layout (keyboard activation is handled in handleStarKeyDown). The
    // always-expanded layout has no key handler, so it selects on every click.
    if (collapsible && event.detail === 0) return;
    setCollapsed(false);
    handleRatingSelect(index, selectedText);
  };

  const handleChangeRating = () => {
    setCollapsed(true);
    setInteractiveExperienceHeader(feedbackExperienceHeader);
  };

  const handleSupportLinkClick = () => {
    if (onClickSupportLink) onClickSupportLink();
  };

  const submitButton = (
    <Button
      disabled={isSubmitDisabled(
        selectedRating,
        commentOptional,
        feedbackComment,
      )}
      big
      onClick={handleSubmit}
    >
      {feedbackButtonTextShort || feedbackButtonText}
    </Button>
  );

  if (collapsible) {
    return (
      <>
        <div className={styles.landscapeContainer}>
          <div
            className={clsx(
              styles.columnLandscape,
              styles.textAlignLeft,
              styles.colSpan5,
              styles.flexCenter,
            )}
          >
            <MediumHeader
              as={enablePanelHeader ? 'p' : 'h2'}
              containerCustomStyle={{ lineHeight: '1.5rem' }}
            >
              {interactiveExperienceHeader ?? feedbackExperienceHeader}
            </MediumHeader>
          </div>
          <div
            className={clsx(
              styles.columnLandscape,
              styles.textAlignRight,
              styles.colSpan7,
            )}
          >
            {collapsed ? (
              <div className={styles.starRatingContainer}>
                <StarRating
                  // we need 2 different handlers for a11y. keyboard
                  // nav events will trigger a click and cause this
                  // layout to move on to the comment section when we
                  // don't want it to.
                  onKeyDown={handleStarKeyDown}
                  onClick={handleStarClick}
                  selectedRating={selectedRating}
                  featureName={featureName}
                  isLandscape
                  displayTextFirst
                />
              </div>
            ) : (
              <div data-test="feedbacklLandscapeCollapsedView">
                <div className={styles.collapsedFormContainer}>
                  <div
                    className={clsx(styles.columnLandscape, styles.colSpan8)}
                  >
                    <TextInput
                      type={SupportedInputType.text}
                      id={commentId}
                      animateLabel
                      label={feedbackCommentLabel || ''}
                      onChange={handleCommentChange}
                    />
                  </div>
                  <div
                    className={clsx(styles.columnLandscape, styles.colSpan4)}
                  >
                    {submitButton}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {!collapsed && (
          <div className={styles.columnLandscape}>
            <div
              className={clsx(styles.columnChangeStarRating, styles.colSpan12)}
            >
              <TextLink
                linkStyle={{
                  sDefault: textLinkStyle,
                  sHover: textLinkHoverStyle,
                }}
                onClick={handleChangeRating}
              >
                {feedbackChangeStarRating}
              </TextLink>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.landscapeContainer}>
      <div className={clsx(styles.feedbackTextContainer, styles.colSpan5)}>
        <PanelContentHeader
          as={enablePanelHeader ? 'p' : 'h2'}
          featureName={featureName}
          headerText={
            interactiveExperienceHeader ?? feedbackExperienceHeader ?? ''
          }
          paragraphChildren={
            <>
              <span className={styles.feedbackBox}>
                {feedbackExperienceBodyText}
              </span>
              {customerSupportLink && (
                <span className={styles.csLink}>
                  <TextLink
                    target={TextLinkTargetType.BLANK}
                    href={customerSupportLink}
                    linkStyle={{
                      sDefault: {
                        textAlign: LEFT,
                        ...textLinkStyle,
                      },
                      sHover: textLinkHoverStyle,
                    }}
                    onClick={handleSupportLinkClick}
                  >
                    {feedbackCustomerSupportLink}
                  </TextLink>
                </span>
              )}
            </>
          }
          isLandscape
        />
      </div>
      <div className={clsx(styles.starRatingContainer, styles.colSpan7)}>
        <StarRating
          onClick={handleStarClick}
          selectedRating={selectedRating}
          featureName={featureName}
          isLandscape
        />
        <div className={styles.feedbackBox}>
          <TextArea
            id={commentId}
            animateLabel
            label={feedbackCommentLabel ?? ''}
            featureName={featureName}
            onChange={handleCommentChange}
          />
        </div>
        <div className={styles.feedbackButton}>{submitButton}</div>
      </div>
    </div>
  );
};

export default Landscape;
